import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Body } from "@hypercard/web-components";
import { useState } from "react";
import { PlaidSuccessResponse } from "./PlaidSuccessMetadata.model";
import { Input } from "@/components/ui/input";
import { z } from "zod";
import { IconBox } from "@common/components";
import { Bank } from "@phosphor-icons/react";
import { useUserStore } from "@domains/auth/utils/useUserStore";

interface Props {
  triggerButtonRef: React.MutableRefObject<HTMLButtonElement | null>;
  onConfirm: (signatoryEmail: string) => void;
  isLoading: boolean;
  plaidResponse?: PlaidSuccessResponse;
}

export const LinkedAccountMandateDialog = ({
  triggerButtonRef,
  onConfirm,
  isLoading,
  plaidResponse,
}: Props) => {
  const [signatoryEmail, setSignatoryEmail] = useState("");

  const handleConfirm = () => {
    onConfirm(signatoryEmail);
    setSignatoryEmail("");
  };

  const isValidEmail = (email: string) => {
    return z.string().email().safeParse(email).success;
  };

  const handleClose = () => {
    setSignatoryEmail("");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSignatoryEmail(e.target.value);
  };

  const userStore = useUserStore.getState();

  const accountName =
    userStore.displayName || plaidResponse?.metadata.account.name || "account";
  const institutionName = plaidResponse?.metadata.institution.name || "Bank";
  const accountNumber = plaidResponse?.metadata.account.mask || "****";

  return (
    <Dialog>
      <DialogTrigger ref={triggerButtonRef}></DialogTrigger>
      <DialogContent className="sm:max-w-max">
        <DialogHeader>
          <DialogTitle>Authorize Direct Debits</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          The agreement below authorizes Airwallex to direct debit from your
          external bank account. You may cancel this authorization at any time
          by deleting the Linked Bank Account.
        </DialogDescription>
        <div className="w-full">
          <div className="mt-2 rounded border border-white/30 p-4">
            <div className="mb-2 flex w-full flex-grow justify-between">
              <Body size="md" weight="regular" className="text-white/70">
                Account Information
              </Body>
            </div>
            <div className="mt-4 flex items-center">
              <IconBox>
                <Bank className="size-7" />
              </IconBox>
              <div className="ml-3 flex flex-col">
                <Body size="lg" weight="medium" className="text-white/90">
                  {institutionName}
                </Body>
                <Body size="sm" weight="regular" className="text-white/70">
                  {`**** ${accountNumber}`}
                </Body>
              </div>
            </div>
          </div>
          <div className="mb-2 mt-6 flex w-full flex-grow justify-between">
            <Body size="sm" weight="regular" className="text-white/70">
              {`Email for ${accountName}`}
            </Body>
          </div>
          <Input
            value={signatoryEmail}
            placeholder={"Enter signatory email"}
            onChange={handleChange}
          />
          <div className="mt-2 flex w-full flex-grow justify-between">
            <Body size="sm" weight="regular" className="text-white/50">
              {`We will send a copy of the Direct Debit Authorization below to this email.`}
            </Body>
          </div>
          <div className="mt-4 flex w-full flex-grow justify-between">
            <Body size="xs" weight="regular" className="text-white/40">
              You authorize Airwallex US, LLC ("Airwallex"), on behalf of
              Untitled Finance, Inc., and, if applicable, Untitled Finance
              Inc.’s affiliated entities ("Merchant"), to debit the bank account
              specified above for any amount owed for charges arising from your
              use of Merchant’s services and/or purchase of products from
              Merchant, pursuant to Merchant’s terms, and, if necessary, to
              electronically credit your bank account to correct erroneous
              debits, until this authorization is revoked. If you use Merchant’s
              services or purchase additional products periodically, pursuant to
              Merchant’s terms, you authorize Airwallex to debit your bank
              account periodically. Payments that fall outside of any regular
              debits authorized above will only be debited after your
              authorization is obtained. You may amend or cancel this
              authorization at any time by providing 30 (thirty) days' notice to
              Airwallex prior to the scheduled debit. You may provide such
              notice to Airwallex by contacting support@airwallex.com.
            </Body>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild disabled={isLoading}>
            <Button
              type="button"
              variant="ghost"
              disabled={isLoading}
              onClick={handleClose}
              className="min-w-[140px]"
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="default"
            disabled={!isValidEmail(signatoryEmail) || isLoading}
            onClick={handleConfirm}
            className="min-w-[140px]"
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
